// Streak tracking utilities for gamification

const STREAK_KEY = 'daydrive_streak';
const LAST_VISIT_KEY = 'daydrive_last_visit';

export interface StreakData {
  currentStreak: number;
  longestStreak: number;
  lastVisitDate: string;
  totalVisits: number;
  isNewDay: boolean;
}

function getToday(): string {
  return new Date().toISOString().split('T')[0];
}

function getDaysDiff(date1: string, date2: string): number {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  const diffTime = Math.abs(d2.getTime() - d1.getTime());
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
}

export function getStreakData(): StreakData {
  const today = getToday();
  const stored = localStorage.getItem(STREAK_KEY);
  const lastVisit = localStorage.getItem(LAST_VISIT_KEY);

  let data: StreakData = {
    currentStreak: 1,
    longestStreak: 1,
    lastVisitDate: today,
    totalVisits: 1,
    isNewDay: true,
  };

  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      data = { ...data, ...parsed };
    } catch {
      // Invalid data, use defaults
    }
  }

  // Check if this is a new day
  if (lastVisit === today) {
    data.isNewDay = false;
    return data;
  }

  // Update streak based on last visit
  if (lastVisit) {
    const daysDiff = getDaysDiff(lastVisit, today);

    if (daysDiff === 1) {
      // Consecutive day - increase streak
      data.currentStreak += 1;
      data.longestStreak = Math.max(data.longestStreak, data.currentStreak);
    } else if (daysDiff > 1) {
      // Streak broken
      data.currentStreak = 1;
    }
  }

  // Update visit data
  data.totalVisits += 1;
  data.lastVisitDate = today;
  data.isNewDay = true;

  // Persist
  localStorage.setItem(STREAK_KEY, JSON.stringify(data));
  localStorage.setItem(LAST_VISIT_KEY, today);

  return data;
}

export function getStreakMessage(streak: number): string {
  if (streak === 1) {
    return "Welcome! Start your productivity journey today.";
  } else if (streak <= 3) {
    return "Great start! Keep the momentum going.";
  } else if (streak <= 7) {
    return "You're on fire! A week of consistency is powerful.";
  } else if (streak <= 14) {
    return "Incredible dedication! You're building a strong habit.";
  } else if (streak <= 30) {
    return "You're unstoppable! A true productivity champion.";
  } else {
    return "Legendary streak! You're inspiring others.";
  }
}

export function getStreakEmoji(streak: number): string {
  if (streak === 1) return '🌱';
  if (streak <= 3) return '🌿';
  if (streak <= 7) return '🔥';
  if (streak <= 14) return '⚡';
  if (streak <= 30) return '🚀';
  return '👑';
}
